import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { EscrowRemoveDocument } from 'src/graphql/actions/escrow/escrowRemove.mutation';
import { ClientError, getApiError } from 'src/utils/errors';
import { refetchAppProtections } from 'src/containers/Applications/Protections/api';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onOpen?: () => void;
  successCallback?: () => void;
  escrowId: string;
  triggerNode?: React.ReactNode;
};

const RemoveDialog = ({ isOpen, onClose, onOpen, successCallback, escrowId, triggerNode }: Props) => {
  const [removeEscrow, { loading }] = useMutation(EscrowRemoveDocument);

  const onRemoveEscrow = async () => {
    try {
      const { data } = await removeEscrow({
        variables: {
          escrowId,
        },
      });
      if (data?.escrowRemove?.success) {
        toast.success('Escrow deleted successfully!');
        refetchAppProtections();
        onClose();
        successCallback?.();
      } else {
        throw new ClientError(data?.escrowRemove?.errors?.[0]);
      }
    } catch (error: unknown) {
      getApiError(error, toast.error);
    }
  };

  const onOpenChange = (open: boolean) => {
    if (open) onOpen?.();
    else onClose();
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogTrigger asChild>{triggerNode}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure you want to delete this escrow?</AlertDialogTitle>
          <AlertDialogDescription>
            The escrow, and all its information, will be removed from your dashboard.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button variant="destructive" onClick={onRemoveEscrow} isLoading={loading}>
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default RemoveDialog;
